import React from 'react';
import Form from 'react-bootstrap/Form';
import { OC_INDICATIONS, sumIndicationVials } from '../../utils/ocRequest';

/**
 * Checkbox list of indications with a vial count input per selected indication.
 * Formik-controlled via selected codes + vials map.
 *
 * @param {Object} props
 * @param {string[]} props.indications
 * @param {Object<string, number|string>} props.indicationVials
 * @param {(next: string[]) => void} props.onIndicationsChange
 * @param {(next: Object) => void} props.onVialsChange
 * @param {boolean} [props.indicationsInvalid]
 * @param {string} [props.indicationsError]
 * @param {string} [props.vialsError]
 * @param {() => void} [props.onBlurIndications]
 * @param {() => void} [props.onBlurVials]
 */
const IndicationVialsPicker = ({
  indications = [],
  indicationVials = {},
  onIndicationsChange,
  onVialsChange,
  indicationsInvalid = false,
  indicationsError,
  vialsError,
  onBlurIndications,
  onBlurVials,
}) => {
  const totalVials = sumIndicationVials(
    Object.fromEntries(
      indications.map(code => [code, Number(indicationVials?.[code]) || 0])
    )
  );

  return (
    <Form.Group>
      <Form.Label>Indication & vials *</Form.Label>
      <div
        role="group"
        aria-label="Indication and vials"
        onBlur={onBlurIndications}
      >
        {OC_INDICATIONS.map(opt => {
          const checked = indications.includes(opt.value);
          return (
            <div
              key={opt.value}
              className="d-flex flex-wrap align-items-center gap-2 mb-2"
            >
              <Form.Check
                type="checkbox"
                id={`indication-${opt.value}`}
                label={opt.label}
                checked={checked}
                isInvalid={indicationsInvalid}
                onChange={e => {
                  if (e.target.checked) {
                    onIndicationsChange([...indications, opt.value]);
                    onVialsChange({
                      ...indicationVials,
                      [opt.value]: indicationVials?.[opt.value] || 1,
                    });
                  } else {
                    onIndicationsChange(indications.filter(v => v !== opt.value));
                    const nextVials = { ...indicationVials };
                    delete nextVials[opt.value];
                    onVialsChange(nextVials);
                  }
                }}
              />
              {checked ? (
                <>
                  <Form.Control
                    type="number"
                    min={1}
                    style={{ width: '7rem' }}
                    aria-label={`Vials for ${opt.label}`}
                    value={indicationVials?.[opt.value] ?? 1}
                    onChange={e => {
                      const n = Number(e.target.value);
                      onVialsChange({
                        ...indicationVials,
                        [opt.value]: Number.isFinite(n) ? n : '',
                      });
                      onBlurVials?.();
                    }}
                  />
                  <span className="text-muted small">vials</span>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
      {indicationsError ? (
        <div className="invalid-feedback d-block">{indicationsError}</div>
      ) : null}
      {vialsError ? (
        <div className="invalid-feedback d-block">{vialsError}</div>
      ) : null}
      {indications.length > 0 ? (
        <Form.Text muted>Total vials requested: {totalVials}</Form.Text>
      ) : null}
    </Form.Group>
  );
};

export default IndicationVialsPicker;
